import axios from 'axios';
import { format, parse } from 'date-fns';
import { PaginationInfo, RequestParams } from 'types/pagination';
import { CollectionFile, TypeItem } from 'types/question';
import { Container, ContainerObject, ChapterItem, RandomSetting, pointObject } from 'types/container';

export const getContainers = async (
    setContainers: (val: Container[]) => void,
    requestParams: RequestParams,
    setPaginationInfo?: (val: PaginationInfo) => void
) => {
    try {
        const params: any = {
            page: requestParams.page,
            per_page: requestParams.per_page,
            sort: `${requestParams.sort[0].sort === 'desc' ? '-' : ''}${requestParams.sort[0].field}`
        };
        requestParams.filters?.forEach((filter) => {
            params[`filter[${filter.name}]`] = filter.value;
        });
        const response = await axios.get('/containers', { params });

        const containerData: Container[] = response.data.containers.data;

        setPaginationInfo?.({
            total: response.data.containers.total,
            last_page: response.data.containers.last_page,
            per_page: response.data.containers.per_page,
            sortable_fields: response.data.containers.sorts,
            available_filters: response.data.containers.filters
        });

        setContainers(containerData);
    } catch (err) {
        console.error(`error in getting containers ${err}`);
    }
};

export const createContainer = async (container: Container, onSuccess: (val: boolean) => void, ongetStatusCode: (val: any) => void) => {
    const formData = new FormData();

    if (container.file) {
        const fileData = container.file as CollectionFile;
        if (fileData?.upload_data) {
            formData.append(
                fileData.file_path
                    .replace(/[^/.]+$/, '')
                    .replace(/\s+/g, '')
                    .replace(/(\.)/g, ''),
                fileData.upload_data
            );
            container.file.file_path = fileData.file_path
                .replace(/[^/.]+$/, '')
                .replace(/\s+/g, '')
                .replace(/(\.)/g, '');
        }
    }

    for (const [key, value] of Object.entries(container)) {
        if (value !== null) {
            if (['file', 'tags', 'level_tags', 'products', 'objects', 'random_settings', 'exampoint'].includes(key)) {
                formData.append(key, JSON.stringify(value));
            } else if (key === 'time') {
                let timestr = value;
                if (timestr.length < 8) {
                    timestr = `00:${value}`;
                }
                const parsedTime = parse(timestr, 'HH:mm:ss', new Date());
                const formattedTime = format(parsedTime, 'yyyy-MM-dd HH:mm:ss');
                formData.append(key, formattedTime);
            } else {
                formData.append(key, value);
            }
        }
    }

    try {
        await axios.post('/containers', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        onSuccess(true);
    } catch (err: any) {
        console.error(`error in create container ${err}`);
        ongetStatusCode(err.response.status);
        onSuccess(false);
    }
};

export const getContainer = async (
    id: string,
    setContainer: (val: Container) => void,
    setObjects: (val: ContainerObject[]) => void,
    setExampoint: (val: pointObject[]) => void,
    setRandomSettings: (val: RandomSetting[]) => void,
    setDisabledProductCategory: (val: any) => void
) => {
    try {
        const response = await axios.get(`/containers/${id}`);
        const container = response.data.container as Container;
        container.type_id = response.data.container.container_type_id;
        // container.tags = response.data.container.tagTree;
        const originalString = container.time;
        if (originalString) {
            container.time = originalString.replace('00:', '');
        }
        setContainer(container);
        setExampoint(response.data.container.examPoint);
        setRandomSettings(response.data.container.randomSettings);
        if (response.data.container.product_category_id) setDisabledProductCategory(true);
        if (
            (response.data.container.container_type_id === 10 || response.data.container.container_type_id === 11) &&
            response.data.container.objects
        ) {
            const updatedObjects = [...response.data.container.objects];

            updatedObjects.forEach((obj: any) => {
                if (!obj || !obj.checked) {
                    obj.type = 'container';
                    obj.checked = true;
                    obj.type_id = response.data.container.container_type_id;
                    obj.isEdited = true;
                }
            });

            setObjects(updatedObjects);
        } else {
            setObjects(response.data.container.objects);
        }
    } catch (err) {
        console.error(`error in getting container ${err}`);
    }
};

export const updateContainer = async (container: Container, onSuccess: (val: boolean) => void, ongetStatusCode: (val: any) => void) => {
    const formData = new FormData();

    if (container.file) {
        const fileData = container.file as CollectionFile;
        if (fileData?.upload_data) {
            formData.append(
                fileData.file_path
                    .replace(/[^/.]+$/, '')
                    .replace(/\s+/g, '')
                    .replace(/(\.)/g, ''),
                fileData.upload_data
            );
            container.file.file_path = fileData.file_path
                .replace(/[^/.]+$/, '')
                .replace(/\s+/g, '')
                .replace(/(\.)/g, '');
        }
    }

    for (const [key, value] of Object.entries(container)) {
        if (value !== null) {
            if (['file', 'tags', 'level_tags', 'products', 'objects', 'random_settings', 'exampoint'].includes(key)) {
                formData.append(key, JSON.stringify(value));
            } else if (key === 'time') {
                let timestr = value;
                if (timestr.length < 8) {
                    timestr = `00:${value}`;
                }
                const parsedTime = parse(timestr, 'HH:mm:ss', new Date());
                const formattedTime = format(parsedTime, 'yyyy-MM-dd HH:mm:ss');
                formData.append(key, formattedTime);
            } else {
                formData.append(key, value);
            }
        }
    }

    formData.append('_method', 'PUT');

    try {
        await axios.post(`/containers/${container.id}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        onSuccess(true);
    } catch (err: any) {
        console.error(`error in update container ${err}`);
        ongetStatusCode(err.response.status);
        onSuccess(false);
    }
};

export const disableContainer = async (id: string) => {
    try {
        await axios.delete(`/containers/${id}`);
    } catch (err) {
        console.error(`error in disable container ${err}`);
    }
};

export const getContainerTypes = async (setContainerTypes: (value: TypeItem[]) => void) => {
    try {
        const response = await axios.get('/container/types');
        const res = response.data.types.filter((o: any) => o.id !== 7 && o.id !== 3);
        setContainerTypes(res);
    } catch (err) {
        console.error(`Error getting container types list ${err}`);
    }
};

export const getChapterTypes = async (setChapterTypes: (value: ChapterItem[]) => void) => {
    try {
        const response = await axios.get('/container/chapters');

        setChapterTypes(response.data.chapters);
    } catch (err) {
        console.error(`Error getting chapter types list ${err}`);
    }
};

export default getContainers;
